import { computed, inject, signal } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable, firstValueFrom, fromEvent, map, takeUntil } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  ErrorBodega, ErrorCategoriaConContenido, ErrorCierreBloqueado, ErrorClaveUsada, ErrorCodigoRepetido, ErrorCodigosInexistentes, ErrorCodigosRegistrados,
  ErrorControlBloqueado, ErrorDia, ErrorEliminacionNoPermitida, ErrorHerramientaEnSalida, ErrorHerramientaNoPrestada, ErrorPendientesSinConfirmar,
  ErrorStockInsuficiente, ErrorUnidadesNoDisponibles, CODIGOS_ERROR_ETAPA_2, LineaSinStock, UnidadNoDisponible,
} from '../models/bodega.model';
import { ErrorOperacion, ProblemaDeOperacion } from '../models/bodega-operaciones.model';
import { LimitadorVentana } from '../../modules/bodega/util/limitador';

// Lo que comparten los clientes HTTP de Bodega (BodegaApiService, por obra) y del Catálogo (CatalogoApiService, global): el cupo de peticiones
// por endpoint, la traducción de cualquier falla a un ErrorBodega en español y el armado de la consulta. Antes vivía todo en BodegaApiService.

export const API = environment.apiUrl;

// El back deja pasar 30 peticiones por minuto por IP Y endpoint; al pasarse BLOQUEA esa clave 60 s
// completos (no se acorta con nuevos intentos) y avisa con la cabecera `Retry-After-global`.
// Por eso el cliente se ordena a sí mismo: no manda más de MAX_POR_MINUTO por endpoint en 60 s
// (esperando si hace falta) y, si aun así llega un 429 —p. ej. otra persona en la misma IP—, espera
// lo que diga el servidor y reintenta UNA vez. Solo se reintenta lo que se puede repetir sin efecto
// doble: lecturas, POST /movimientos (lleva claveCliente) y la generación de código interno (idempotente).
const MAX_POR_MINUTO = 26;
const VENTANA_MS = 60_000;
const ESPERA_LIMITE_MS = 61_000;
const ESPERA_MIN_MS = 1_000;
const ESPERA_MAX_MS = 65_000;
const REINTENTOS_LIMITE = 1;

const MSJ_SIN_CONEXION = 'Sin conexión con el servidor. Revisa tu señal e inténtalo de nuevo.';
const MSJ_LIMITE = 'Hay muchas consultas seguidas. Espera unos segundos (hasta un minuto) y vuelve a intentarlo.';
const MSJ_SESION = 'Tu sesión expiró. Inicia sesión nuevamente.';
const MSJ_SIN_PERMISO = 'No tienes permiso para realizar esta acción.';
const MSJ_NO_ENCONTRADO = 'No se encontró lo que buscabas. Puede que ya no exista o que no tengas acceso a esta obra.';
const MSJ_CONFLICTO = 'La operación choca con el estado actual de la bodega. Actualiza la pantalla e inténtalo de nuevo.';

// Textos por defecto de Nest (en inglés): nunca se muestran tal cual al usuario.
const MENSAJES_GENERICOS = new Set([
  'unauthorized', 'forbidden', 'not found', 'bad request', 'conflict', 'internal server error',
  'too many requests', 'service unavailable', 'bad gateway', 'gateway timeout', 'payload too large',
]);

export function mensajeDelCuerpo(cuerpo: unknown): string | null {
  const bruto = (cuerpo as { message?: unknown } | null | undefined)?.message;
  const texto = Array.isArray(bruto)
    ? bruto.filter((m): m is string => typeof m === 'string').join(' ')
    : typeof bruto === 'string' ? bruto : '';
  const limpio = texto.trim();
  return limpio && !MENSAJES_GENERICOS.has(limpio.toLowerCase()) ? limpio : null;
}

/** Las piezas que no estaban disponibles (409 de una entrega), o null si el cuerpo no las trae. */
function unidadesNoDisponibles(cuerpo: unknown): UnidadNoDisponible[] | null {
  const lista = (cuerpo as { unidades?: unknown } | null | undefined)?.unidades;
  if (!Array.isArray(lista) || lista.length === 0) return null;
  if (!lista.every(u => u && typeof u.codigo === 'string' && typeof u.estado === 'string')) return null;
  return lista.map((u: Partial<UnidadNoDisponible>) => ({
    codigo: String(u.codigo),
    estado: u.estado as UnidadNoDisponible['estado'],
    estadoEtiqueta: String(u.estadoEtiqueta ?? ''),
    producto: String(u.producto ?? ''),
  }));
}

/** La lista `codigos` de un error del servidor (409 de alta, 404 de entrega o devolución), o null si no trae. */
function listaDeCodigos(cuerpo: unknown): string[] | null {
  const codigos = (cuerpo as { codigos?: unknown } | null | undefined)?.codigos;
  return Array.isArray(codigos) && codigos.length > 0 && codigos.every(c => typeof c === 'string') ? (codigos as string[]) : null;
}

/** El `code` legible por máquina que trae el cuerpo de un error (null si no trae). */
export function codigoDelCuerpo(cuerpo: unknown): string | null {
  const c = (cuerpo as { code?: unknown } | null | undefined)?.code;
  return typeof c === 'string' && c ? c : null;
}

/** Una lista de textos de un campo del cuerpo (vacía si no viene o no son textos). */
export function listaDeTextos(cuerpo: unknown, campo: string): string[] {
  const lista = (cuerpo as Record<string, unknown> | null | undefined)?.[campo];
  return Array.isArray(lista) ? lista.filter((x): x is string => typeof x === 'string') : [];
}

const numeroDelCuerpo = (cuerpo: unknown, campo: string): number => {
  const v = Number((cuerpo as Record<string, unknown> | null | undefined)?.[campo]);
  return Number.isFinite(v) ? v : 0;
};

function lineasSinStock(cuerpo: unknown): LineaSinStock[] | null {
  const lineas = (cuerpo as { lineas?: unknown } | null | undefined)?.lineas;
  if (!Array.isArray(lineas)) return null;
  return lineas.map((l: Partial<LineaSinStock>) => ({
    productoId: Number(l.productoId),
    nombre: String(l.nombre ?? ''),
    disponible: Number(l.disponible),
    solicitado: Number(l.solicitado),
  }));
}

/**
 * Convierte cualquier falla HTTP en un ErrorBodega con mensaje en español. Los 409 con datos
 * propios salen como subclases tipadas: ErrorStockInsuficiente (lineas[]), ErrorCodigoRepetido
 * (productoId del dueño del código), ErrorCategoriaConContenido (hijos, productos e impide[]) y
 * ErrorEliminacionNoPermitida (impedimentos[] y sugerencia). El resto trae status, mensaje y, si el
 * servidor mandó un `code` que las pantallas reconocen (los de la etapa 2: PREFIJO_REPETIDO,
 * CATEGORIA_CICLO…), ese `code`.
 */
export function traducirErrorBodega(err: unknown, porDefecto: string): ErrorBodega {
  if (err instanceof ErrorBodega) return err;
  if (!(err instanceof HttpErrorResponse)) return new ErrorBodega(0, porDefecto, true);

  const { status } = err;
  // En una descarga de blob el cuerpo del error llega como Blob: no trae mensaje legible.
  const cuerpo: unknown = err.error instanceof Blob ? null : err.error;
  const delServidor = mensajeDelCuerpo(cuerpo);

  if (status === 0) return new ErrorBodega(0, MSJ_SIN_CONEXION, true);
  if (status === 429) return new ErrorBodega(429, MSJ_LIMITE);
  if (status === 401) return new ErrorBodega(401, delServidor ?? MSJ_SESION);

  // Los códigos de la etapa 2 se reconocen primero y por su `code`: sus cuerpos traen datos propios que no deben confundirse con los de los
  // otros 409 (que se distinguen por la forma del cuerpo).
  const codigo = codigoDelCuerpo(cuerpo);
  if (codigo && status >= 400 && status < 500 && (CODIGOS_ERROR_ETAPA_2 as readonly string[]).includes(codigo)) {
    if (codigo === 'CATEGORIA_CON_CONTENIDO') {
      return new ErrorCategoriaConContenido(
        delServidor ?? 'No se puede eliminar la categoría: todavía tiene contenido.',
        numeroDelCuerpo(cuerpo, 'hijos'), numeroDelCuerpo(cuerpo, 'productos'), listaDeTextos(cuerpo, 'impide'),
      );
    }
    if (codigo === 'ELIMINACION_NO_PERMITIDA') {
      const sugerencia = (cuerpo as { sugerencia?: unknown } | null)?.sugerencia;
      return new ErrorEliminacionNoPermitida(
        delServidor ?? 'No se puede eliminar: ya tiene historia.', listaDeTextos(cuerpo, 'impedimentos'), typeof sugerencia === 'string' && sugerencia ? sugerencia : null,
      );
    }
    return new ErrorBodega(status, delServidor ?? porDefecto, false, codigo);
  }

  if (status === 409) {
    const lineas = lineasSinStock(cuerpo);
    if (lineas) return new ErrorStockInsuficiente(delServidor ?? 'Stock insuficiente.', lineas);
    const dueno = cuerpo as { codigo?: unknown; productoId?: unknown; productoNombre?: unknown } | null;
    if (typeof dueno?.productoId === 'number' && typeof dueno.codigo === 'string') {
      return new ErrorCodigoRepetido(
        delServidor ?? 'Ese código ya pertenece a otro producto.',
        dueno.codigo, dueno.productoId, String(dueno.productoNombre ?? ''),
      );
    }
    // Cambiar el control de un producto que ya tiene stock, unidades o préstamos: el servidor dice cuántos.
    const bloqueo = cuerpo as { obrasConStock?: unknown; unidades?: unknown; prestamosAbiertos?: unknown } | null;
    if (typeof bloqueo?.obrasConStock === 'number') {
      return new ErrorControlBloqueado(
        delServidor ?? 'No se puede cambiar el control del producto: ya tiene existencias.',
        bloqueo.obrasConStock,
        typeof bloqueo.unidades === 'number' ? bloqueo.unidades : 0,
        typeof bloqueo.prestamosAbiertos === 'number' ? bloqueo.prestamosAbiertos : 0,
      );
    }
    // Piezas ya etiquetadas cuyo código ya existe: el servidor dice cuáles.
    const codigos = listaDeCodigos(cuerpo);
    if (codigos) return new ErrorCodigosRegistrados(delServidor ?? 'Esos códigos ya están registrados.', codigos);
    // Devolución: una herramienta ya devuelta o que no figura como prestada (trae su línea y, si es una pieza, su código).
    const lineaResuelta = cuerpo as { lineaId?: unknown; codigo?: unknown } | null;
    if (typeof lineaResuelta?.lineaId === 'number') {
      return new ErrorHerramientaNoPrestada(delServidor ?? 'Esa herramienta ya no figura como prestada.', typeof lineaResuelta.codigo === 'string' ? lineaResuelta.codigo : null, lineaResuelta.lineaId);
    }
    // Entrega: estas piezas no estaban disponibles (prestadas, en mantención o dadas de baja).
    const noDisponibles = unidadesNoDisponibles(cuerpo);
    if (noDisponibles) return new ErrorUnidadesNoDisponibles(delServidor ?? 'Algunas herramientas no están disponibles.', noDisponibles);
    // Día de bodega: operar sin día abierto, abrir uno ya cerrado, cerrar con pendientes… (trae un código legible por máquina)
    const delDia = errorDelDia(cuerpo, delServidor);
    if (delDia) return delDia;
    const claveUsada = mensajeClaveUsada(delServidor);
    if (claveUsada) return new ErrorClaveUsada(claveUsada);
    return new ErrorBodega(409, delServidor ?? MSJ_CONFLICTO, false, codigo ?? undefined);
  }

  if (status === 403) return new ErrorBodega(403, delServidor ?? MSJ_SIN_PERMISO, false, codigo ?? undefined);
  if (status === 404) {
    // Entrega o devolución por código: el servidor dice cuáles no existen en esta obra.
    const inexistentes = listaDeCodigos(cuerpo);
    if (inexistentes) return new ErrorCodigosInexistentes(delServidor ?? 'Esos códigos no se encontraron en esta obra.', inexistentes);
    return new ErrorBodega(404, delServidor ?? MSJ_NO_ENCONTRADO, false, codigo ?? undefined);
  }
  if (status === 413) return new ErrorBodega(413, 'El archivo es demasiado grande (máximo 5 MB).');
  // Una salida con herramientas: el servidor dice cuáles (el carrito entero se rechazó).
  if (status === 400 && codigo === 'HERRAMIENTA_VA_POR_ENTREGA') {
    return new ErrorHerramientaEnSalida(delServidor ?? 'Las herramientas se entregan en préstamo: usa Entrega.', herramientasDeSalida(cuerpo));
  }
  // 400 y similares traen el motivo en español (class-validator); 5xx nunca se muestra crudo.
  if (status >= 500) return new ErrorBodega(status, porDefecto, true);
  return new ErrorBodega(status, delServidor ?? porDefecto, false, codigo ?? undefined);
}

const MSJ_CLAVE_USADA_OPERACION = 'Este envío ya se había hecho antes con otro contenido y es probable que se haya registrado. Revisa el Historial antes de volver a armarlo.';
const MSJ_SIN_PERMISO_OPERACION = 'No tienes permiso para registrar alguna de las líneas de esta operación (devolver herramientas pide el permiso de retirar; ingresar material, el de ingresar).';

/** Los problemas de un error de operación: cada uno con su código, mensaje, línea (desde 0, o null si es general) y el resto de sus datos. */
function problemasDeOperacion(cuerpo: unknown): ProblemaDeOperacion[] {
  const lista = (cuerpo as { errores?: unknown } | null | undefined)?.errores;
  if (!Array.isArray(lista)) return [];
  return lista
    .filter((p): p is Record<string, unknown> => !!p && typeof p === 'object' && typeof (p as { code?: unknown }).code === 'string')
    .map(p => {
      const { code, message, linea, ...datos } = p;
      return { codigo: String(code), mensaje: String(message ?? ''), linea: typeof linea === 'number' ? linea : null, datos };
    });
}

/**
 * Como traducirErrorBodega pero para las tres operaciones (llegada de stock, devolución, salida): un rechazo de una operación trae
 * `errores[]` con TODOS los problemas y la línea (desde 0) de cada uno, y sale como ErrorOperacion para poder marcar la línea que falló.
 * La clave ya usada con otro contenido sale como ErrorClaveUsada (congela el carrito) y los errores del día (sin `errores`) siguen siendo
 * ErrorDia. Una operación rechazada no guardó nada.
 */
export function traducirErrorDeOperacion(err: unknown, porDefecto: string): ErrorBodega {
  if (err instanceof ErrorBodega) return err;
  if (err instanceof HttpErrorResponse && (err.status === 400 || err.status === 404 || err.status === 409)) {
    const cuerpo: unknown = err.error instanceof Blob ? null : err.error;
    const c = cuerpo as { code?: unknown; linea?: unknown } | null;
    const problemas = problemasDeOperacion(cuerpo);
    if (typeof c?.code === 'string' && problemas.length > 0) {
      if (c.code === 'CLAVE_USADA') return new ErrorClaveUsada(MSJ_CLAVE_USADA_OPERACION);
      return new ErrorOperacion(err.status, mensajeDelCuerpo(cuerpo) ?? porDefecto, c.code, typeof c.linea === 'number' ? c.linea : null, problemas);
    }
  }
  if (err instanceof HttpErrorResponse && err.status === 403) {
    const delServidor = mensajeDelCuerpo(err.error instanceof Blob ? null : err.error);
    if (!delServidor || /no tienes acceso a esta obra/i.test(delServidor)) return new ErrorBodega(403, MSJ_SIN_PERMISO_OPERACION);
  }
  return traducirErrorBodega(err, porDefecto);
}

const CODIGOS_DEL_DIA: ReadonlySet<string> = new Set([
  'DIA_CERRADO', 'DIA_ANTERIOR_ABIERTO', 'DIA_YA_CERRADO', 'PENDIENTES_SIN_CONFIRMAR', 'CIERRE_BLOQUEADO', 'SIN_DIA_ABIERTO',
  'SIN_DIA_CERRADO_HOY', 'HAY_OTRO_DIA_ABIERTO', 'CLAVE_USADA', 'CONFLICTO_SIMULTANEO',
]);

/** El 409 del día que explica el cuerpo (por su código), o null si no es del día. */
function errorDelDia(cuerpo: unknown, delServidor: string | null): ErrorDia | null {
  const c = cuerpo as { code?: unknown; fechaDia?: unknown; hoy?: unknown; fecha?: unknown; prestamos?: unknown; total?: unknown; pendientes?: unknown } | null;
  if (typeof c?.code !== 'string' || !CODIGOS_DEL_DIA.has(c.code)) return null;
  const codigo = c.code as ErrorDia['codigo'];
  const mensaje = delServidor ?? 'No se pudo completar la operación del día.';
  if (codigo === 'CIERRE_BLOQUEADO') {
    const prestamos = Array.isArray(c.prestamos) ? (c.prestamos as ErrorCierreBloqueado['prestamos']) : [];
    return new ErrorCierreBloqueado(mensaje, prestamos, typeof c.total === 'number' ? c.total : prestamos.length, typeof c.fecha === 'string' ? c.fecha : undefined);
  }
  if (codigo === 'PENDIENTES_SIN_CONFIRMAR' && c.pendientes && typeof c.pendientes === 'object') {
    return new ErrorPendientesSinConfirmar(mensaje, c.pendientes as ErrorPendientesSinConfirmar['pendientes']);
  }
  return new ErrorDia(mensaje, codigo, {
    ...(typeof c.fechaDia === 'string' ? { fechaDia: c.fechaDia } : {}),
    ...(typeof c.hoy === 'string' ? { hoy: c.hoy } : {}),
    ...(typeof c.fecha === 'string' ? { fecha: c.fecha } : {}),
  });
}

/** Las herramientas que el servidor rechazó en una salida (productoId y nombre); vacío si no vienen o no se entienden. */
function herramientasDeSalida(cuerpo: unknown): { productoId: number; nombre: string }[] {
  const lista = (cuerpo as { productos?: unknown } | null)?.productos;
  if (!Array.isArray(lista)) return [];
  return lista
    .filter((p): p is { productoId: number; nombre?: unknown } => typeof (p as { productoId?: unknown })?.productoId === 'number')
    .map(p => ({ productoId: p.productoId, nombre: String(p.nombre ?? '') }));
}

/**
 * Si el 409 es «esa clave ya se usó con otro contenido» (el reintento de una operación que el servidor ya
 * guardó, pero esta vez con datos distintos), el mensaje para quien la hizo; null si es otro 409. Cada operación
 * lleva su propia clave y su propio texto: carrito, producto, alta de unidades, entrega y devolución.
 */
export function mensajeClaveUsada(textoDelServidor: string | null | undefined): string | null {
  const m = /clave de (carrito|producto|alta|pr[eé]stamo|devoluci[oó]n)\b.*ya se us[oó] con otro/i.exec(textoDelServidor ?? '');
  if (!m) return null;
  switch (m[1].toLowerCase()) {
    case 'producto':
      return 'Este producto ya se había enviado antes con otros datos y es probable que se haya creado. Búscalo en el catálogo antes de volver a crearlo.';
    case 'alta':
      return 'Esta alta de unidades ya se había enviado antes con otro contenido y es probable que se haya registrado. Revísalo en la lista de unidades antes de repetirla.';
    case 'préstamo': case 'prestamo':
      return 'Esta entrega ya se había enviado antes con otro contenido y es probable que se haya registrado. Confírmalo en Préstamos antes de volver a armarla.';
    case 'devolución': case 'devolucion':
      return 'Esta devolución ya se había enviado antes con otro contenido y es probable que se haya registrado. Confírmala en el préstamo antes de repetirla.';
    default:
      return 'Este carrito ya se había enviado antes con otro contenido y es probable que se haya registrado. Confírmalo en el Historial o con quien administra la bodega antes de volver a armarlo.';
  }
}

/**
 * Espera pedida por el servidor, en segundos. El throttler del back nombra su limitador «global»,
 * así que la cabecera real es `Retry-After-global`; también se acepta `Retry-After`. Sin cabecera
 * (o ilegible) se espera el bloqueo completo de 60 s. Siempre acotada.
 */
export function esperaDeReintento(err: HttpErrorResponse): number {
  const segundos = Number(err.headers?.get('Retry-After-global') ?? err.headers?.get('Retry-After'));
  const ms = Number.isFinite(segundos) && segundos > 0 ? segundos * 1000 : ESPERA_LIMITE_MS;
  return Math.min(ESPERA_MAX_MS, Math.max(ESPERA_MIN_MS, ms));
}

export type ValorParam = string | number | boolean | null | undefined;

/** Query string sin claves vacías; los booleanos viajan como 1 y solo cuando están activos. */
export function parametros(valores: Record<string, ValorParam>): HttpParams {
  let params = new HttpParams();
  for (const [clave, valor] of Object.entries(valores)) {
    if (valor === undefined || valor === null || valor === false) continue;
    if (valor === true) { params = params.set(clave, '1'); continue; }
    const texto = String(valor).trim();
    if (texto) params = params.set(clave, texto);
  }
  return params;
}

/** Se lanza cuando se cancela una consulta (el filtro cambió): no es una falla, solo que ya no hace falta la respuesta. */
export class ErrorCancelado extends ErrorBodega {
  constructor() {
    super(0, 'Consulta cancelada.');
    this.name = 'ErrorCancelado';
  }
}

/** Un observable que emite cuando la señal se cancela (corta la petición en curso). */
function cancelacion(senal: AbortSignal): Observable<unknown> {
  return senal.aborted ? new Observable(o => { o.next(true); o.complete(); }) : fromEvent(senal, 'abort').pipe(map(() => true));
}

/**
 * El núcleo que comparten los dos clientes HTTP (Bodega y Catálogo): el cupo por endpoint, el reintento acotado tras un 429 y la traducción de
 * las fallas a ErrorBodega en español. Cada cliente tiene su propia instancia (y sus propios cupos): el back cuenta por IP y endpoint, y los
 * endpoints de uno y otro son distintos.
 */
export abstract class ClienteApiConLimite {
  protected http = inject(HttpClient);

  /** Cuántas llamadas están esperando por un 429; la pantalla puede avisar «esperando unos segundos…». */
  private esperando = signal(0);
  readonly esperandoLimite = computed(() => this.esperando() > 0);

  private limitadores = new Map<string, LimitadorVentana>();

  /** Lo que el cliente hace con cada falla ya traducida, antes de lanzarla (Bodega cuenta los choques con el día cerrado). */
  protected alFallar(_error: ErrorBodega): void { /* nada por defecto */ }

  /**
   * Hace la llamada respetando el cupo del endpoint (`clave`); ante un 429 reintenta de forma acotada
   * solo si `reintentable`.
   */
  protected async pedir<T>(
    clave: string, llamada: () => Observable<T>, reintentable: boolean, porDefecto: string,
    traductor: (err: unknown, porDefecto: string) => ErrorBodega = traducirErrorBodega, senal?: AbortSignal,
  ): Promise<T> {
    for (let intento = 0; ; intento++) {
      // Con cupo, la petición sale en el acto (sin ceder el turno); sin cupo, espera a que lo haya.
      if (!this.tomarCupo(clave)) await this.esperarCupo(clave);
      try {
        return await firstValueFrom(senal ? llamada().pipe(takeUntil(cancelacion(senal))) : llamada());
      } catch (err) {
        // Una consulta que la pantalla canceló (cambió el filtro) no es un fallo: quien la pidió ya no espera su respuesta.
        if (senal?.aborted) throw new ErrorCancelado();
        if (reintentable && err instanceof HttpErrorResponse && err.status === 429 && intento < REINTENTOS_LIMITE) {
          await this.avisarEspera(esperaDeReintento(err));
          continue;
        }
        const traducido = traductor(err, porDefecto);
        this.alFallar(traducido);
        throw traducido;
      }
    }
  }

  /** Toma un cupo de la ventana de 60 s del endpoint si lo hay; false si ya está lleno. */
  private tomarCupo(clave: string): boolean {
    let limitador = this.limitadores.get(clave);
    if (!limitador) {
      limitador = new LimitadorVentana(MAX_POR_MINUTO, VENTANA_MS);
      this.limitadores.set(clave, limitador);
    }
    if (limitador.espera() > 0) return false;
    limitador.registrar();
    return true;
  }

  /** Espera a que el endpoint tenga cupo y lo toma. */
  private async esperarCupo(clave: string): Promise<void> {
    const limitador = this.limitadores.get(clave)!;
    while (!this.tomarCupo(clave)) {
      await this.avisarEspera(limitador.espera() + 100);
    }
  }

  private async avisarEspera(ms: number): Promise<void> {
    this.esperando.update(n => n + 1);
    try {
      await this.esperar(ms);
    } finally {
      this.esperando.update(n => n - 1);
    }
  }

  /** Aparte para poder reemplazarlo en las pruebas. */
  protected esperar(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

